import { useEffect, useState } from 'react';

import { DeleteOutlined as DeleteOutlinedIcon, Edit as EditIcon } from '@mui/icons-material';
import { HttpStatusCode } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { ConfirmationDialog, FormDialog, ResourceNotFoundFallback } from '~components';
import { useSnackbar } from '~hooks';
import { DetailsGridLayout, usePageLayoutProperties } from '~layouts';

import AccessNfcTagsCard from '../components/cards/accessNfcTagsCard';
import AccessUsersCard from '../components/cards/accessUsersCard';
import AccessUserGroupGeneralInfoCard from '../components/cards/userGroupGeneralInfoCard';
import SkcUserGroupForm from '../forms/skcUserGroupForm/skcUserGroupForm';
import { SkcUserGroupNew } from '../interfaces/skcUserGroup';
import SkcUserGroupsService from '../services/skcUserGroupsService';

const service = new SkcUserGroupsService();

interface UserAction {
	type: 'delete' | 'edit';
	label?: string;
	id: string;
}

interface SkcUserGroupDetailsContainerProps {
	id: string;
}

const SkcUserGroupDetailsContainer = ({ id }: SkcUserGroupDetailsContainerProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { enqueueSuccessSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();
	const { setPageTitle, setPageHeaderButtons: setPageButtons } = usePageLayoutProperties();

	const [userAction, setUserAction] = useState<UserAction | null>(null);

	const { data, error } = useSWR(
		[service.basePath, id],
		([_, args]) => service.getUserGroupById(args),
		{
			onSuccess: (res) => {
				console.log(res);
				setPageTitle(res.label);
			},
			onDiscarded: () => console.log('discarded'),
			onError: () => console.log('error'),
		}
	);

	const { trigger: triggerEdit, isMutating: isEditMutating } = useSWRMutation(
		[service.basePath, id],
		([_, id], { arg }: { arg: SkcUserGroupNew }) => service.updateCompleteUserGroup(id, arg, data),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(
					`${t('ui.success.message.updated')} ${t('accessGroup').toLowerCase()}`
				);
				setUserAction(null);
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { trigger: triggerDelete, isMutating: isDeleteMutating } = useSWRMutation(
		[service.basePath, id],
		([_, id]) => service.deleteUserGroup(id),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.successfullyDeleted')} ${t('accessGroup').toLowerCase()}`);
				navigate('..', { relative: 'path' });
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	useEffect(() => {
		setPageButtons([
			{
				children: t('ui.edit'),
				startIcon: <EditIcon />,
				onClick: () => setUserAction({ type: 'edit', id: id }),
			},
			{
				children: t('ui.delete'),
				color: 'error',
				startIcon: <DeleteOutlinedIcon />,
				onClick: () => setUserAction({ type: 'delete', id: id }),
			},
		]);
	}, [id, setPageButtons, t]);

	if (error?.status === HttpStatusCode.NotFound) {
		return <ResourceNotFoundFallback label={id} type={t('accessGroup')} />;
	}

	return (
		<>
			<DetailsGridLayout>
				<AccessUserGroupGeneralInfoCard id={id} />
				<AccessUsersCard id={id} />
				<AccessNfcTagsCard id={id} />
			</DetailsGridLayout>
			<FormDialog
				title={t('editResource', { resource: t('accessGroup') })}
				open={userAction?.type === 'edit'}
				loading={isEditMutating}
				maxWidth='lg'
				fullWidth
				onClose={() => setUserAction(null)}
			>
				<SkcUserGroupForm id={id} onSubmit={async (data) => await triggerEdit(data)} />
			</FormDialog>
			<ConfirmationDialog
				subTitle={t('thisWillDeleteResourceWithName', {
					resource: t('deviceGroup'),
					name: userAction?.label ?? userAction?.id,
				})}
				open={userAction?.type === 'delete'}
				loading={isDeleteMutating}
				onClose={() => setUserAction(null)}
				onConfirm={async () => await triggerDelete()}
			/>
		</>
	);
};

export default SkcUserGroupDetailsContainer;
